import React, { useEffect, useState, Children, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ShieldCheck, BookOpen, ShoppingCart, GlassWater, Download, Lock, Zap, HeartPulse, AlertCircle, ArrowRight, X } from 'lucide-react';
// Meta Pixel type declaration
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// Reusable Animation Variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const pulseAnimation = {
  scale: [1, 1.02, 1],
  boxShadow: ['0px 0px 0px rgba(232, 114, 42, 0)', '0px 0px 20px rgba(232, 114, 42, 0.4)', '0px 0px 0px rgba(232, 114, 42, 0)'],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut'
  }
};
// Reusable CTA Button Component
const CHECKOUT_URL = 'https://www.betterdailyguides.com/cart/51826786402606:1';
const CTAButton = ({
  children,
  className = '',
  href = CHECKOUT_URL
}: {
  children: React.ReactNode;
  className?: string;
  href?: string;
}) => {
  const handleClick = () => {
    if (href !== '#pricing' && window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: '14 Días para Desinflamar tu Cuerpo',
        content_type: 'product',
        value: 6.39,
        currency: 'USD'
      });
    }
  };
  return <motion.a href={href} onClick={handleClick} whileHover={{
    scale: 1.05
  }} whileTap={{
    scale: 0.95
  }} animate={pulseAnimation} className={`inline-flex items-center justify-center w-full md:w-auto px-8 py-5 text-xl font-bold text-white bg-brand-orange rounded-xl shadow-lg hover:bg-orange-600 transition-colors ${className}`} data-id="element-1">
      {children}
      <ArrowRight className="ml-2 w-6 h-6" data-id="element-2" />
    </motion.a>;
};
export function SalesLetter() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  // Initialize Meta Pixel and fire PageView
  useEffect(() => {
    // Load Meta Pixel script
    if (!window.fbq) {
      const n: any = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!window._fbq) window._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript?.parentNode?.insertBefore(script, firstScript);
    }
    window.fbq('init', '26472237642370868');
    window.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-brand-cream-light font-body text-[#444] leading-relaxed" data-id="element-3">
      {/* Meta Pixel noscript fallback */}
      <noscript data-id="element-4">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=26472237642370868&ev=PageView&noscript=1" alt="" data-id="element-5" />
      </noscript>

      {/* 1. HERO SECTION — New punchy copy */}
      <section className="pt-10 sm:pt-16 pb-8 sm:pb-12 px-5 sm:px-6 bg-brand-cream" data-id="element-6">
        <div className="max-w-3xl mx-auto" data-id="element-7">
          {/* Text content — left-aligned on mobile for natural reading */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="text-center" data-id="element-8">
            <p className="text-lg sm:text-xl text-brand-dark/70 font-body font-semibold mb-2 tracking-wide" data-id="element-9">
              No es gordura:
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-brand-dark leading-[1.1] mb-4" data-id="element-10">
              es <span className="text-brand-green italic" data-id="element-11">hinchazón.</span>
            </h1>
            <p className="text-xl sm:text-2xl md:text-3xl font-heading text-brand-dark/80 mb-4 leading-snug" data-id="element-12">
              14 días para sentir tu cintura otra vez.
            </p>
            <p className="text-base sm:text-lg text-gray-500 mb-8" data-id="element-13">
              Sin dietas extremas, sin ejercicios dolorosos.
            </p>

            {/* CTA Button */}
            <div className="mb-4" data-id="element-14">
              <CTAButton href="#pricing" className="w-full sm:w-auto text-lg sm:text-xl px-10 py-5" data-id="element-15">
                Empezar mi plan
              </CTAButton>
            </div>

            {/* Trust note */}
            <p className="text-xs sm:text-sm text-gray-400 italic" data-id="element-16">
              * Diseñado específicamente para tu metabolismo actual
            </p>
          </motion.div>

          {/* Product image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="relative mx-auto max-w-sm sm:max-w-md mt-10" data-id="element-17">
            <div className="absolute inset-0 bg-brand-gold/20 blur-3xl rounded-full transform -translate-y-4" data-id="element-18"></div>
            <img src="https://cdn.magicpatterns.com/uploads/bN5H6fXUPKCwCS8MuQbDpS/Generated_Image_March_06,_2026_-_6_52PM.png" alt="14 Días para Desinflamar tu Cuerpo Después de los 40" className="relative z-10 w-full h-auto rounded-2xl shadow-2xl border-4 border-white" data-id="element-19" />
          </motion.div>
        </div>
      </section>

      {/* BRIDGE — Old headline, mobile-optimized */}
      <section className="py-8 sm:py-10 px-5 sm:px-6 bg-brand-cream-light" data-id="element-20">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="max-w-xl mx-auto text-center" data-id="element-21">
          <p className="text-lg sm:text-xl md:text-2xl font-heading italic text-brand-dark/70 leading-relaxed" data-id="element-22">
            Si tienes más de 40 y sientes que tu cuerpo está inflamado todo el
            tiempo,{' '}
            <span className="text-brand-green font-bold not-italic" data-id="element-23">
              sigue leyendo…
            </span>
          </p>
        </motion.div>
      </section>

      {/* 2. PAIN POINTS */}
      <section className="py-16 px-6 bg-brand-sage" data-id="element-24">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-3xl mx-auto" data-id="element-25">
          <p className="text-xl md:text-2xl mb-8 text-center font-heading text-brand-dark" data-id="element-26">
            Tal vez últimamente has notado que tu cuerpo ya no responde como
            antes.
          </p>

          <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-brand-green/10" data-id="element-27">
            <p className="text-lg font-bold text-brand-dark mb-6" data-id="element-28">
              Quizás sientes:
            </p>

            <motion.ul variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="space-y-4 mb-8" data-id="element-29">
              {['inflamación abdominal frecuente', 'digestión lenta', 'aumento de peso inesperado', 'cansancio durante el día', 'falta de energía'].map((item, i) => <motion.li key={i} variants={fadeInUp} className="flex items-start" data-id="element-30">
                  <AlertCircle className="w-6 h-6 text-brand-orange mr-3 flex-shrink-0 mt-0.5" data-id="element-31" />
                  <span className="text-lg" data-id="element-32">{item}</span>
                </motion.li>)}
            </motion.ul>

            <p className="text-lg italic text-brand-dark/80 border-l-4 border-brand-gold pl-4" data-id="element-33">
              Y lo más frustrante es que muchas veces parece que no importa lo
              que hagas, nada cambia.
            </p>
          </div>
        </motion.div>
      </section>

      {/* 3. EXPLANATION */}
      <section className="py-16 px-6 bg-white" data-id="element-34">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-3xl mx-auto space-y-6 text-lg" data-id="element-35">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-brand-dark mb-6" data-id="element-36">
            Pero aquí hay algo importante que debes saber.
          </h2>
          <p data-id="element-37">
            Muchas mujeres no se dan cuenta de que después de los 40 el cuerpo
            cambia naturalmente.
          </p>
          <p data-id="element-38">
            El metabolismo se vuelve más lento. La digestión puede volverse más
            sensible. Y ciertos alimentos que antes no causaban problemas ahora
            pueden provocar inflamación constante.
          </p>
          <div className="bg-brand-sage/50 p-6 rounded-xl mt-8" data-id="element-39">
            <p className="font-bold text-brand-green text-xl text-center" data-id="element-40">
              La buena noticia es que hay una forma simple de ayudar a tu cuerpo
              a recuperar su equilibrio.
            </p>
          </div>
        </motion.div>
      </section>

      {/* 4. SOLUTION INTRODUCTION */}
      <section className="py-20 px-6 bg-brand-cream text-center" data-id="element-41">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-3xl mx-auto" data-id="element-42">
          <p className="text-xl text-brand-dark mb-4 font-heading italic" data-id="element-43">
            Un plan simple para ayudar a tu cuerpo a desinflamarse en 14 días
          </p>
          <p className="text-lg mb-8" data-id="element-44">Creamos una guía práctica llamada:</p>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-brand-green mb-10 leading-tight" data-id="element-45">
            14 DÍAS PARA DESINFLAMAR TU CUERPO DESPUÉS DE LOS 40
          </h2>

          <div className="space-y-4 text-lg max-w-2xl mx-auto" data-id="element-46">
            <p data-id="element-47">
              Un plan fácil de seguir que te muestra cómo mejorar tu
              alimentación y adoptar hábitos simples que ayudan a tu cuerpo a
              sentirse mejor.
            </p>
            <p className="font-bold text-brand-dark" data-id="element-48">
              Sin dietas extremas. Sin suplementos costosos. Sin métodos
              complicados.
            </p>
            <p className="text-xl text-brand-green font-heading italic mt-6" data-id="element-49">
              Solo cambios simples que pueden marcar una gran diferencia.
            </p>
          </div>
        </motion.div>
      </section>

      {/* 5. WHAT YOU'LL LEARN */}
      <section className="py-16 px-6 bg-white" data-id="element-50">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-3xl mx-auto" data-id="element-51">
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-brand-dark mb-8 text-center" data-id="element-52">
            Durante estos 14 días aprenderás cómo:
          </h3>

          <div className="bg-brand-sage p-8 rounded-2xl shadow-sm mb-8" data-id="element-53">
            <motion.ul variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="space-y-5" data-id="element-54">
              {['reducir la inflamación abdominal', 'mejorar tu digestión', 'recuperar tu energía', 'crear hábitos saludables sostenibles'].map((item, i) => <motion.li key={i} variants={fadeInUp} className="flex items-center text-lg md:text-xl font-medium text-brand-dark" data-id="element-55">
                  <CheckCircle2 className="w-8 h-8 text-brand-green mr-4 flex-shrink-0" data-id="element-56" />
                  {item}
                </motion.li>)}
            </motion.ul>
          </div>

          <p className="text-center text-lg italic text-gray-600" data-id="element-57">
            Todo utilizando alimentos simples que puedes encontrar fácilmente en
            cualquier supermercado.
          </p>
        </motion.div>
      </section>

      {/* 6. DESIGNED FOR REAL WOMEN */}
      <section className="py-16 px-6 bg-brand-cream-light border-y border-brand-cream" data-id="element-58">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-3xl mx-auto text-center" data-id="element-59">
          <HeartPulse className="w-12 h-12 text-brand-gold mx-auto mb-6" data-id="element-60" />
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-brand-dark mb-8" data-id="element-61">
            Este programa fue diseñado para mujeres reales
          </h3>
          <div className="space-y-4 text-lg" data-id="element-62">
            <p data-id="element-63">No necesitas experiencia previa.</p>
            <p data-id="element-64">No necesitas cocinar recetas complicadas.</p>
            <p className="font-bold text-brand-green text-xl mt-6" data-id="element-65">
              Este plan fue creado para ser práctico, sencillo y fácil de
              seguir, incluso si tienes una vida ocupada.
            </p>
          </div>
        </motion.div>
      </section>

      {/* 7. WHAT'S INCLUDED */}
      <section className="py-16 px-6 bg-white" data-id="element-66">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-3xl mx-auto" data-id="element-67">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-dark mb-10 text-center" data-id="element-68">
            Esto es lo que recibirás hoy
          </h2>

          <div className="bg-white rounded-2xl shadow-xl border-t-8 border-brand-green overflow-hidden" data-id="element-69">
            <div className="p-8 md:p-10" data-id="element-70">
              <div className="flex items-center mb-6" data-id="element-71">
                <BookOpen className="w-10 h-10 text-brand-green mr-4" data-id="element-72" />
                <h3 className="text-xl md:text-2xl font-bold text-brand-dark" data-id="element-73">
                  Ebook principal
                </h3>
              </div>

              <h4 className="text-2xl md:text-3xl font-heading font-bold text-brand-green mb-6" data-id="element-74">
                14 DÍAS PARA DESINFLAMAR TU CUERPO DESPUÉS DE LOS 40
              </h4>

              <p className="text-lg font-bold mb-4 text-gray-700" data-id="element-75">Incluye:</p>
              <ul className="space-y-3" data-id="element-76">
                {['plan completo de alimentación de 14 días', 'alimentos que ayudan a reducir inflamación', 'hábitos simples para mejorar digestión', 'consejos para mantener resultados'].map((item, i) => <li key={i} className="flex items-start text-lg" data-id="element-77">
                    <span className="text-brand-green font-bold mr-3" data-id="element-78">•</span>
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </motion.div>
      </section>

      {/* 9. PRICING SECTION (Conversion Point) */}
      <section id="pricing" className="py-20 px-6 bg-brand-cream" data-id="element-79">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-3xl mx-auto text-center" data-id="element-80">
          <p className="text-xl text-gray-500 mb-2" data-id="element-81">
            Todo esto normalmente tendría un valor de{' '}
            <span className="line-through" data-id="element-82">$24.99</span>
          </p>
          <p className="text-2xl font-heading font-bold text-brand-dark mb-6" data-id="element-83">
            Pero hoy puedes obtener acceso completo por solo:
          </p>

          {/* Discount badge */}
          <div className="inline-block bg-brand-orange/10 border border-brand-orange/30 rounded-full px-5 py-2 mb-6" data-id="element-84">
            <span className="text-brand-orange font-bold text-sm sm:text-base" data-id="element-85">
              🔥 ¡74% DE DESCUENTO HOY!
            </span>
          </div>

          {/* Price display */}
          <div className="mb-10" data-id="element-86">
            <span className="text-7xl md:text-8xl font-bold text-brand-green drop-shadow-sm" data-id="element-87">
              $6.39
            </span>
          </div>

          <div className="mb-6" data-id="element-88">
            <CTAButton data-id="element-89">DESCARGAR AHORA</CTAButton>
          </div>

          <p className="text-lg font-bold text-brand-dark mb-4" data-id="element-90">
            Acceso inmediato
          </p>
          <p className="text-gray-600 mb-2" data-id="element-91">
            Después de tu compra recibirás acceso inmediato para descargar el
            ebook y todos los bonos.
          </p>
          <p className="text-brand-green font-bold text-lg" data-id="element-92">
            Puedes comenzar hoy mismo.
          </p>
        </motion.div>
      </section>

      {/* 10. WHO THIS HELPS */}
      <section className="py-16 px-6 bg-white" data-id="element-93">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-2xl mx-auto" data-id="element-94">
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-brand-dark mb-8 text-center" data-id="element-95">
            Este programa puede ayudarte si:
          </h3>

          <ul className="space-y-4" data-id="element-96">
            {['tienes más de 40 años', 'sientes inflamación frecuente', 'quieres mejorar tu digestión', 'buscas hábitos simples para sentirte mejor'].map((item, i) => <li key={i} className="flex items-center bg-brand-sage/30 p-4 rounded-lg" data-id="element-97">
                <CheckCircle2 className="w-6 h-6 text-brand-green mr-4 flex-shrink-0" data-id="element-98" />
                <span className="text-lg text-brand-dark font-medium" data-id="element-99">
                  {item}
                </span>
              </li>)}
          </ul>
        </motion.div>
      </section>

      {/* 11. GUARANTEE */}
      <section className="py-16 px-6 bg-brand-sage" data-id="element-100">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-2xl mx-auto bg-white p-8 md:p-10 rounded-2xl shadow-lg border-2 border-brand-green/20 text-center relative overflow-hidden" data-id="element-101">
          <div className="absolute top-0 left-0 w-full h-2 bg-brand-green" data-id="element-102"></div>
          <ShieldCheck className="w-16 h-16 text-brand-green mx-auto mb-6" data-id="element-103" />
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-brand-dark mb-4" data-id="element-104">
            Garantía de satisfacción
          </h3>
          <p className="text-lg mb-4 text-gray-700" data-id="element-105">
            Queremos que te sientas segura con tu compra.
          </p>
          <p className="text-lg font-medium text-brand-dark" data-id="element-106">
            Si el contenido no cumple tus expectativas, puedes solicitar un
            reembolso dentro de los primeros 7 días.
          </p>
        </motion.div>
      </section>

      {/* 12. FINAL CTA */}
      <section className="py-20 px-6 bg-brand-cream border-t border-brand-gold/20" data-id="element-107">
        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="max-w-3xl mx-auto text-center" data-id="element-108">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-dark mb-6" data-id="element-109">
            Tu bienestar comienza con pequeños cambios
          </h2>

          <div className="space-y-4 text-lg mb-10 text-gray-700" data-id="element-110">
            <p data-id="element-111">
              No necesitas hacer cambios radicales para comenzar a sentirte
              mejor.
            </p>
            <p data-id="element-112">
              A veces, pequeñas mejoras en la alimentación y hábitos diarios
              pueden generar una gran diferencia.
            </p>
            <p className="text-2xl font-heading font-bold text-brand-green mt-8" data-id="element-113">
              Comienza hoy
            </p>
            <p data-id="element-114">
              Haz clic en el botón de abajo y obtén acceso inmediato al
              programa.
            </p>
          </div>

          <div className="mb-8" data-id="element-115">
            <CTAButton className="w-full md:w-auto md:px-12 md:py-6 md:text-2xl" data-id="element-116">
              DESCARGAR AHORA
            </CTAButton>
          </div>

          <p className="text-xl font-heading font-bold text-brand-dark mb-8" data-id="element-117">
            14 DÍAS PARA DESINFLAMAR TU CUERPO
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-sm text-gray-500 font-medium" data-id="element-118">
            <div className="flex items-center" data-id="element-119">
              <Zap className="w-4 h-4 mr-2" data-id="element-120" />
              Acceso inmediato
            </div>
            <div className="hidden md:block" data-id="element-121">•</div>
            <div className="flex items-center" data-id="element-122">
              <Lock className="w-4 h-4 mr-2" data-id="element-123" />
              Pago seguro
            </div>
            <div className="hidden md:block" data-id="element-124">•</div>
            <div className="flex items-center" data-id="element-125">
              <Download className="w-4 h-4 mr-2" data-id="element-126" />
              Descarga digital
            </div>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 px-6 bg-brand-dark text-center" data-id="element-127">
        <div className="flex items-center justify-center gap-6 text-sm" data-id="element-128">
          <button onClick={() => setActiveModal('privacy')} className="text-white/60 hover:text-white transition-colors underline underline-offset-2" data-id="element-129">
            Privacy Policy
          </button>
          <span className="text-white/30" data-id="element-130">|</span>
          <button onClick={() => setActiveModal('terms')} className="text-white/60 hover:text-white transition-colors underline underline-offset-2" data-id="element-131">
            Terms of Service
          </button>
        </div>
        <p className="text-white/30 text-xs mt-4" data-id="element-132">
          © {new Date().getFullYear()} 14 Días para Desinflamar. Todos los
          derechos reservados.
        </p>
      </footer>

      {/* LEGAL MODALS */}
      <AnimatePresence data-id="element-133">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-134">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-135">
              {/* Modal header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl" data-id="element-136">
                <h3 className="text-xl font-heading font-bold text-brand-dark" data-id="element-137">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" data-id="element-138">
                  <X className="w-5 h-5" data-id="element-139" />
                </button>
              </div>

              {/* Modal content */}
              <div className="p-6 text-sm text-gray-600 leading-relaxed space-y-4" data-id="element-140">
                {activeModal === 'privacy' ? <>
                    <p className="font-bold text-gray-800" data-id="element-141">
                      Última actualización:{' '}
                      {new Date().toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </p>
                    <p data-id="element-142">
                      Tu privacidad es importante para nosotros. Esta política
                      describe cómo recopilamos, usamos y protegemos tu
                      información personal.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-143">
                      Información que recopilamos
                    </h4>
                    <p data-id="element-144">
                      Recopilamos información que nos proporcionas directamente,
                      como tu nombre y dirección de correo electrónico al
                      realizar una compra. También recopilamos información de
                      pago a través de nuestros procesadores de pago seguros.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-145">
                      Uso de la información
                    </h4>
                    <p data-id="element-146">
                      Utilizamos tu información para procesar tu compra,
                      enviarte el producto digital adquirido y comunicarnos
                      contigo sobre tu pedido. No compartimos tu información
                      personal con terceros con fines de marketing.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-147">
                      Protección de datos
                    </h4>
                    <p data-id="element-148">
                      Implementamos medidas de seguridad técnicas y
                      organizativas para proteger tu información personal contra
                      acceso no autorizado, pérdida o alteración.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-149">
                      Tus derechos
                    </h4>
                    <p data-id="element-150">
                      Tienes derecho a acceder, corregir o eliminar tu
                      información personal en cualquier momento. Para ejercer
                      estos derechos, puedes contactarnos directamente.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-151">Cookies</h4>
                    <p data-id="element-152">
                      Utilizamos cookies esenciales para el funcionamiento del
                      sitio y cookies analíticas para mejorar tu experiencia.
                      Puedes gestionar tus preferencias de cookies en la
                      configuración de tu navegador.
                    </p>
                  </> : <>
                    <p className="font-bold text-gray-800" data-id="element-153">
                      Última actualización:{' '}
                      {new Date().toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </p>
                    <p data-id="element-154">
                      Al acceder y utilizar este sitio web y adquirir nuestros
                      productos digitales, aceptas estos términos y condiciones.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-155">
                      Producto digital
                    </h4>
                    <p data-id="element-156">
                      El producto ofrecido es un ebook digital con contenido
                      educativo e informativo sobre bienestar y alimentación.
                      Este contenido no sustituye el consejo médico profesional.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-157">Entrega</h4>
                    <p data-id="element-158">
                      Tras completar tu compra, recibirás acceso inmediato para
                      descargar el ebook y todos los materiales bonus incluidos
                      en formato digital.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-159">
                      Política de reembolso
                    </h4>
                    <p data-id="element-160">
                      Ofrecemos una garantía de satisfacción de 7 días. Si el
                      contenido no cumple tus expectativas, puedes solicitar un
                      reembolso completo dentro de los primeros 7 días
                      posteriores a tu compra.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-161">
                      Propiedad intelectual
                    </h4>
                    <p data-id="element-162">
                      Todo el contenido, incluyendo textos, imágenes y
                      materiales del ebook, está protegido por derechos de
                      autor. No está permitida la reproducción, distribución o
                      reventa sin autorización expresa.
                    </p>
                    <h4 className="font-bold text-gray-800 pt-2" data-id="element-163">
                      Limitación de responsabilidad
                    </h4>
                    <p data-id="element-164">
                      Los resultados pueden variar de persona a persona. El
                      contenido es de carácter informativo y educativo. Consulta
                      siempre con un profesional de la salud antes de realizar
                      cambios significativos en tu alimentación.
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}